import type { ComponentType } from "react";
import type { WeatherCondition } from "@/types";
import { ClearDaySky } from "@/app/components/weather/backgrounds/ClearDaySky";
import { CloudyDaySky } from "@/app/components/weather/backgrounds/CloudyDaySky";
import { RainyDaySky } from "@/app/components/weather/backgrounds/RainyDaySky";
import { ClearNightSky } from "@/app/components/weather/backgrounds/ClearNightSky";
import { CloudyNightSky } from "@/app/components/weather/backgrounds/CloudyNightSky";
import { RainyNightSky } from "@/app/components/weather/backgrounds/RainyNightSky";
import { FrostSky } from "@/app/components/weather/backgrounds/FrostSky";

/** Identifiers for each full-viewport sky illustration variant. */
export type SkyBackgroundVariant =
  | "clear-day"
  | "cloudy-day"
  | "rainy-day"
  | "clear-night"
  | "cloudy-night"
  | "rainy-night"
  | "frost";

const SKY_COMPONENTS: Record<SkyBackgroundVariant, ComponentType> = {
  "clear-day": ClearDaySky,
  "cloudy-day": CloudyDaySky,
  "rainy-day": RainyDaySky,
  "clear-night": ClearNightSky,
  "cloudy-night": CloudyNightSky,
  "rainy-night": RainyNightSky,
  frost: FrostSky,
};

/**
 * Maps live weather condition and day/night flag to a sky illustration variant.
 */
export function resolveSkyBackground(
  condition: WeatherCondition,
  isDay: boolean,
): SkyBackgroundVariant {
  if (condition === "frost") return "frost";
  if (condition === "rain" || condition === "storm") {
    return isDay ? "rainy-day" : "rainy-night";
  }
  if (!isDay) {
    if (condition === "night") return "clear-night";
    if (condition === "cloud" || condition === "wind" || condition === "mist" || condition === "cold") {
      return "cloudy-night";
    }
    return "cloudy-night";
  }
  if (condition === "sun" || condition === "heat") return "clear-day";
  if (condition === "cloud" || condition === "wind" || condition === "mist" || condition === "cold") {
    return "cloudy-day";
  }
  return "cloudy-day";
}

/**
 * Returns the React component for a resolved sky background variant.
 */
export function getSkyBackgroundComponent(variant: SkyBackgroundVariant): ComponentType {
  return SKY_COMPONENTS[variant];
}

export { ClearDaySky } from "@/app/components/weather/backgrounds/ClearDaySky";
export { CloudyDaySky } from "@/app/components/weather/backgrounds/CloudyDaySky";
export { RainyDaySky } from "@/app/components/weather/backgrounds/RainyDaySky";
export { ClearNightSky } from "@/app/components/weather/backgrounds/ClearNightSky";
export { CloudyNightSky } from "@/app/components/weather/backgrounds/CloudyNightSky";
export { RainyNightSky } from "@/app/components/weather/backgrounds/RainyNightSky";
export { FrostSky } from "@/app/components/weather/backgrounds/FrostSky";
